import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppIdea } from '@/hooks/app-library';
import { capitalize } from '@/lib/utils';

interface InfoItemProps {
  label: string;
  value: string | null | undefined;
  icon?: React.ReactNode;
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value, icon }) => (
  value ? (
    <div className="flex items-center gap-3 p-3 bg-background/50 dark:bg-background/80 rounded-2xl border">
      {icon && <div className="text-muted-foreground flex-shrink-0">{icon}</div>}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-foreground">{label}</div>
        <div className="text-sm text-muted-foreground truncate">{value}</div>
      </div>
    </div>
  ) : null
);

const LongTextItem: React.FC<{ label: string; value: string; icon: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="p-3 bg-background/50 dark:bg-background/80 rounded-2xl border">
    <div className="flex items-start gap-3">
      <div className="text-muted-foreground flex-shrink-0 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-foreground mb-1">{label}</div>
        <div className="text-sm text-muted-foreground leading-relaxed">
          {value}
        </div>
      </div>
    </div>
  </div>
);

interface AppIdeaParametersSectionProps {
  idea: AppIdea;
  onCloseCurrentLightbox?: () => void;
}

export const AppIdeaParametersSection: React.FC<AppIdeaParametersSectionProps> = ({ 
  idea, 
  onCloseCurrentLightbox 
}) => {
  const navigate = useNavigate();
  const hasCitations = idea.research_citations && idea.research_citations.length > 0;



  // Event-driven communication - Listen for research generation events
  useEffect(() => {
    const handleResearchGenerated = async (event: CustomEvent) => {
      const { researchId, question, originId, appIdeaUpdated, error } = event.detail;
      
      console.log('[AppIdeaParametersSection] Research generated event received:', event.detail);
      console.log('[AppIdeaParametersSection] Expected origin:', `app_idea_${idea.id}`);
      console.log('[AppIdeaParametersSection] Actual origin:', originId);
      
      // Check if this event is for our app idea
      if (originId === `app_idea_${idea.id}`) {
        console.log('[AppIdeaParametersSection] Event is for this app idea, processing...');
        
        if (appIdeaUpdated) {
          console.log('[AppIdeaParametersSection] App idea database was updated successfully by hook');
        } else if (error) {
          console.error('[AppIdeaParametersSection] Hook failed to update app idea database:', error);
        }
        
        // Close current lightbox
        if (onCloseCurrentLightbox) {
          console.log('[AppIdeaParametersSection] Closing app ideas lightbox');
          onCloseCurrentLightbox();
        }
        
        // Navigate to research generator with specific research
        console.log('[AppIdeaParametersSection] Navigating to research generator');
        navigate(`/research-generator?research=${researchId}`);
      } else {
        console.log('[AppIdeaParametersSection] Event is not for this app idea, ignoring');
      }
    };

    // Add event listener for the universal research generated event
    window.addEventListener('universalResearchGenerated', handleResearchGenerated as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('universalResearchGenerated', handleResearchGenerated as EventListener);
    };
  }, [idea.id, onCloseCurrentLightbox, navigate]);

  return (
    <div className="space-y-3">
      {/* Flat structure - each item is a direct child */}
      
      {/* Basic Info Items */}
      <InfoItem 
        label="Category" 
        value={idea.category ? capitalize(idea.category) : 'N/A'} 
        icon="🏷️"
      />
      
      <InfoItem 
        label="Priority" 
        value={idea.priority} 
        icon="⭐"
      />
      
      <InfoItem 
        label="Platform" 
        value={idea.platform} 
        icon="💻"
      />

      {/* Research Items */}
      {hasCitations && (
        <div className="flex items-center gap-3 p-3 bg-background/50 dark:bg-background/80 rounded-2xl border">
          <div className="text-muted-foreground">📚</div>
          <div className="flex-1">
            <div className="font-medium text-sm text-foreground">Sources</div>
            <div className="text-sm text-muted-foreground">
              {idea.research_citations!.length} source{idea.research_citations!.length !== 1 ? 's' : ''} referenced
            </div>
          </div>
        </div>
      )}

      {/* Long Text Items */}
      {idea.original_problem && (
        <LongTextItem
          label="Original Problem"
          value={idea.original_problem}
          icon="❓"
        />
      )}

      {idea.current_solution && (
        <LongTextItem
          label="Current Solution"
          value={idea.current_solution}
          icon="💡"
        />
      )}

    </div>
  );
};
